import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError } from '../api/client';
import {
  RENDER_UNAVAILABLE,
  isOffsetPage,
  reportApi,
  type ExportResult,
  type ReportColumn,
  type ReportDescriptor,
  type ReportFormat,
  type ReportPageInfo,
  type ReportRow,
  type ReportSummary,
  type ReportType,
} from '../api/reportClient';
import { DateField } from '../components/DateField';
import { EmptyState } from '../components/EmptyState';
import { PageHeading } from '../components/PageHeading';
import { PageLoader } from '../components/PageLoader';
import { CursorPagePager, OffsetPager } from '../components/Pagination';
import { ReportSummaryCards } from '../components/ReportSummaryCards';
import { SelectMenu } from '../components/SelectMenu';
import { formatDateTime, formatIsoDate } from '../lib/dateFormat';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { localizeReportCell } from '../lib/localizeReportCell';
import { MoneyParseError, formatUsageCost } from '../lib/money';
import { fadeUp } from '../lib/motionPresets';
import { useSession } from '../lib/SessionContext';

const DEFAULT_DAYS = 30;
const DEFAULT_PAGE_SIZE = 20;

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
// The date inputs are calendar days; the contract wants RFC 3339 instants.
// A plain UTC-day interpretation, same as Usage — clearly documented so nobody
// mistakes it for more precision than it has.
function rangeStart(dateStr: string): string {
  return `${dateStr}T00:00:00Z`;
}
function rangeEnd(dateStr: string): string {
  return `${dateStr}T23:59:59Z`;
}

/**
 * Render one cell according to the column type the SERVER declared.
 *
 * Money arrives as a 6-decimal string (contracts/README.md). On-screen we use
 * the same `formatUsageCost` as the Usage page — two places once the amount is
 * at least one satang (so spend_daily "0.027524" reads as "0.03" on both
 * pages), and full six places only for sub-satang charges that would otherwise
 * look like free. Exports keep the server's full precision; this is display
 * only, never via `Number`/`toLocaleString`.
 *
 * String enums (`kind`, `status`, `doc_type`) are glossed through
 * `localizeReportCell` so Thai UI never shows raw Postgres enum text.
 */
function cell(
  value: unknown,
  column: ReportColumn,
  reports: ReturnType<typeof useLanguage>['dict']['reports'],
): string {
  if (value === null || value === undefined) return '—';
  switch (column.type) {
    case 'money': {
      try {
        return formatUsageCost(String(value));
      } catch (e) {
        if (e instanceof MoneyParseError) return String(value);
        throw e;
      }
    }
    case 'number':
      return Number(value).toLocaleString();
    case 'date':
      return formatIsoDate(String(value));
    case 'timestamp':
      return formatDateTime(String(value));
    default:
      return localizeReportCell(column.key, String(value), reports);
  }
}

export function Reports() {
  const { ready } = useSession();
  const { dict, locale } = useLanguage();

  const today = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(() => isoDate(new Date(today.getTime() - (DEFAULT_DAYS - 1) * 86_400_000)));
  const [to, setTo] = useState(() => isoDate(today));

  const [catalog, setCatalog] = useState<ReportDescriptor[] | null>(null);
  const [selected, setSelected] = useState<ReportType | null>(null);

  const [columns, setColumns] = useState<ReportColumn[]>([]);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [page, setPage] = useState<ReportPageInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Offset reports track a page number; keyset reports track the cursor that
  // fetched each visited page (index 0 = first page, no cursor). Keeping the
  // trail rather than a single cursor is what makes "previous" work at all —
  // a keyset cursor only ever points forward.
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const cursors = useRef<(string | undefined)[]>([undefined]);

  const [exporting, setExporting] = useState<ReportFormat | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [readyFile, setReadyFile] = useState<ExportResult | null>(null);

  const descriptor = catalog?.find((d) => d.report_type === selected) ?? null;
  const isKeyset = descriptor?.paging === 'keyset';

  useEffect(() => {
    const ac = new AbortController();
    reportApi
      .catalog(locale === 'th' ? 'th' : 'en', ac.signal)
      .then((r) => {
        setCatalog(r.data);
        setSelected((prev) => prev ?? r.data[0]?.report_type ?? null);
      })
      .catch((e) => {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setCatalog([]);
        setError(dict.reports.loadError);
      });
    return () => ac.abort();
  }, [locale, dict.reports.loadError]);

  const run = useCallback(
    async (targetPage: number, size = pageSize) => {
      if (!selected || !descriptor) return;
      setLoading(true);
      setError(null);
      try {
        const result = await reportApi.run(selected, {
          dateFrom: rangeStart(from),
          dateTo: rangeEnd(to),
          locale: locale === 'th' ? 'th' : 'en',
          ...(descriptor.paging === 'keyset'
            ? { limit: size, cursor: cursors.current[targetPage - 1] }
            : { page: targetPage, pageSize: size }),
        });
        setColumns(result.columns);
        setRows(result.data as ReportRow[]);
        setSummary(result.summary ?? null);
        setPage(result.page);
        setPageIndex(targetPage);

        if (descriptor.paging === 'keyset' && !isOffsetPage(result.page)) {
          // Remember the cursor that reaches the NEXT page, so a later
          // "previous" can replay its way back.
          cursors.current[targetPage] = result.page.next_cursor ?? undefined;
        }
        setLoadedOnce(true);
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setError(e instanceof ApiError ? e.problem.detail || dict.reports.loadError : dict.reports.loadError);
      } finally {
        setLoading(false);
      }
    },
    [selected, descriptor, from, to, locale, pageSize, dict.reports.loadError],
  );

  // Changing the report or the range invalidates the cursor trail: a cursor is
  // only meaningful for the query that produced it.
  const resetAndRun = useCallback(() => {
    cursors.current = [undefined];
    setReadyFile(null);
    setExportError(null);
    void run(1);
  }, [run]);

  // Column headings are server-owned and locale-specific. Switching EN/TH
  // already refreshes catalogue titles, but a previously-run table would keep
  // its old headings (Thai columns under English chrome). Drop the stale
  // result and re-run so the table matches the chrome.
  const localeBoot = useRef(locale);
  const loadedOnceRef = useRef(loadedOnce);
  loadedOnceRef.current = loadedOnce;
  useEffect(() => {
    if (localeBoot.current === locale) return;
    localeBoot.current = locale;
    const shouldRerun = loadedOnceRef.current && selected != null;
    setColumns([]);
    setRows([]);
    setSummary(null);
    setPage(null);
    setLoadedOnce(false);
    setError(null);
    cursors.current = [undefined];
    setReadyFile(null);
    setExportError(null);
    if (shouldRerun) {
      void run(1);
    }
  }, [locale, selected, run]);

  const doExport = useCallback(
    async (format: ReportFormat) => {
      if (!selected) return;
      setExporting(format);
      setExportError(null);
      setReadyFile(null);
      try {
        const result = await reportApi.exportFile(selected, format, {
          dateFrom: rangeStart(from),
          dateTo: rangeEnd(to),
          locale: locale === 'th' ? 'th' : 'en',
        });
        setReadyFile(result);
        // LLMR-95/96 (Excel/PDF): the export button IS the download — a customer should
        // not have to click twice. download_url answers with
        // Content-Disposition: attachment (report-api), so this never
        // navigates the page away, same reasoning as the invoice download
        // fix in TopUp.tsx. The note below is a fallback if the browser's
        // download setting intercepts this, not a required second step.
        window.location.href = result.download_url;
      } catch (e) {
        if (e instanceof ApiError && e.is(RENDER_UNAVAILABLE)) {
          // 503 from report-api: the renderer is down, not the request. Say so,
          // and point at the export that does not need Chromium.
          setExportError(dict.reports.renderUnavailable);
        } else {
          setExportError(e instanceof ApiError ? e.problem.detail || dict.reports.exportError : dict.reports.exportError);
        }
      } finally {
        setExporting(null);
      }
    },
    [selected, from, to, locale, dict.reports],
  );

  if (!ready || catalog === null) {
    return (
      <div className="page reports-page">
        <PageHeading subtitle={dict.reports.subtitle}>{dict.common.reports}</PageHeading>
        <PageLoader />
      </div>
    );
  }

  if (catalog.length === 0) {
    return (
      <div className="page reports-page">
        <PageHeading subtitle={dict.reports.subtitle}>{dict.common.reports}</PageHeading>
        <EmptyState title={dict.reports.emptyTitle}>{dict.reports.noReports}</EmptyState>
      </div>
    );
  }

  const offsetPage = page && isOffsetPage(page) ? page : null;
  const showLoader = loading && rows.length === 0;
  const showResults = loadedOnce || loading;

  return (
    <div className="page reports-page">
      <PageHeading subtitle={dict.reports.subtitle}>{dict.common.reports}</PageHeading>

      <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel usage-panel usage-panel--filters">
        <div className="usage-filters">
          <div className="inline-form-field">
            <label htmlFor="report-type">{dict.reports.reportLabel}</label>
            <SelectMenu
              id="report-type"
              aria-label={dict.reports.reportLabel}
              value={(selected ?? '') as ReportType}
              onChange={(v) => {
                setSelected(v);
                setRows([]);
                setSummary(null);
                setPage(null);
                setLoadedOnce(false);
                cursors.current = [undefined];
                setReadyFile(null);
              }}
              options={catalog.map((d) => ({ value: d.report_type, label: d.title }))}
            />
          </div>
          <div className="inline-form-field">
            <label htmlFor="report-from">{dict.reports.fromLabel}</label>
            <DateField id="report-from" value={from} max={to} rangeEnd={to} onChange={setFrom} aria-label={dict.reports.fromLabel} />
          </div>
          <div className="inline-form-field">
            <label htmlFor="report-to">{dict.reports.toLabel}</label>
            <DateField
              id="report-to"
              value={to}
              min={from}
              max={isoDate(today)}
              rangeStart={from}
              onChange={setTo}
              aria-label={dict.reports.toLabel}
            />
          </div>
          <div className="inline-form-field">
            <button type="button" className="primary" disabled={loading || !selected} onClick={resetAndRun}>
              {loading ? dict.reports.running : dict.reports.run}
            </button>
          </div>
        </div>

        {descriptor?.description && <p className="note">{descriptor.description}</p>}

        <div className="usage-filters">
          <button
            type="button"
            className="ghost"
            disabled={exporting !== null || !selected}
            onClick={() => void doExport('xlsx')}
          >
            {exporting === 'xlsx' ? dict.reports.exporting : dict.reports.exportExcel}
          </button>
          <button
            type="button"
            className="ghost"
            disabled={exporting !== null || !selected}
            onClick={() => void doExport('pdf')}
          >
            {exporting === 'pdf' ? dict.reports.exporting : dict.reports.exportPdf}
          </button>
        </div>

        {exportError && <div className="err" role="alert">{exportError}</div>}

        {readyFile && (
          <p className="note" role="status">
            {dict.reports.downloadReady(readyFile.file_name)}{' '}
            {/*
              A plain link to the presigned URL, opened in a new tab. It is a
              signed object-storage URL, not an app route, so it must not go
              through the router — and it carries no session cookie, which is
              why it is short-lived. Fetching it does not consume it: the same
              link can be retried until it expires.
            */}
            <a href={readyFile.download_url} target="_blank" rel="noopener noreferrer">
              {dict.reports.download}
            </a>{' '}
            <span className="note">
              · {dict.reports.rowCount(readyFile.row_count ?? 0)} ·{' '}
              {dict.reports.linkExpires(formatDateTime(readyFile.expires_at))}
            </span>
          </p>
        )}
      </motion.section>

      {showResults && (
      <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel usage-panel">
        {error && <div className="err" role="alert">{error}</div>}

        {showLoader && <PageLoader />}

        {!error && !showLoader && loadedOnce && summary && <ReportSummaryCards summary={summary} />}

        {!error && !showLoader && loadedOnce && rows.length === 0 && (
          <EmptyState title={dict.reports.emptyTitle}>{dict.reports.emptyBody}</EmptyState>
        )}

        {!showLoader && rows.length > 0 && (
          <div className="table-scroll">
            <table role="table" className="usage-table">
              <thead role="rowgroup">
                <tr role="row">
                  {columns.map((c) => (
                    <th
                      role="columnheader"
                      key={c.key}
                      className={c.type === 'money' || c.type === 'number' ? 'num' : undefined}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody role="rowgroup">
                {rows.map((row, i) => (
                  <tr role="row" key={i}>
                    {columns.map((c) => (
                      <td
                        role="cell"
                        key={c.key}
                        data-label={c.label}
                        className={[
                          c.type === 'money' || c.type === 'number' ? 'num' : undefined,
                          c.type === 'money' && c.key === 'cost' ? 'report-cost-cell' : undefined,
                        ]
                          .filter(Boolean)
                          .join(' ') || undefined}
                      >
                        {cell(row[c.key], c, dict.reports)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/*
          The control follows the SHAPE the server returned, not a preference.
          A keyset list cannot honestly render "page 3 of 12" — the server has
          not counted and will not (ADR-0009).
        */}
        {!showLoader &&
          (isKeyset
            ? rows.length > 0 && (
                <CursorPagePager
                  page={page && !isOffsetPage(page) ? page : null}
                  loaded={rows.length}
                  pageIndex={pageIndex}
                  busy={loading}
                  onPrev={() => void run(pageIndex - 1)}
                  onNext={() => void run(pageIndex + 1)}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    // A cursor is only meaningful for the query that produced it,
                    // and page size is part of that query — so changing it starts
                    // the walk again rather than resuming mid-trail.
                    setPageSize(size);
                    cursors.current = [undefined];
                    // Pass size explicitly: setState has not committed yet, so
                    // run's closed-over pageSize would still be the old value.
                    void run(1, size);
                  }}
                />
              )
            : offsetPage && (
                <OffsetPager
                  page={offsetPage}
                  busy={loading}
                  onPage={(p) => void run(p)}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    void run(1, size);
                  }}
                />
              ))}
      </motion.section>
      )}
    </div>
  );
}
